<template>
  <div>
    <el-dialog title="培训统计" :visible.sync="dialogVisible" width="1200px" @close="handleClose">
      <div v-if="trainingData" class="training-statistics">
        <div class="statistics-header">
          <h3>{{ trainingData.title }}</h3>
          <div class="statistics-meta">
            <span>参与司机：{{ trainingData.joinCount || 0 }}人</span>
            <span>创建时间：{{ trainingData.createTime }}</span>
          </div>
        </div>

        <!-- 统计概览 -->
        <div class="statistics-overview">
          <el-row :gutter="20">
            <el-col :span="6">
              <div class="stat-card">
                <div class="stat-number">{{ statistics.totalDrivers || 0 }}</div>
                <div class="stat-label">总参与人数</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-card">
                <div class="stat-number">{{ statistics.completed || 0 }}</div>
                <div class="stat-label">已完成人数</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-card">
                <div class="stat-number">{{ statistics.passed || 0 }}</div>
                <div class="stat-label">通过人数</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-card">
                <div class="stat-number">{{ statistics.completionRate }}%</div>
                <div class="stat-label">完成率</div>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- 状态分布 -->
        <div class="status-distribution">
          <h4>培训状态分布</h4>
          <el-row :gutter="20">
            <el-col :span="4">
              <div class="status-item">
                <div class="status-number">{{ statistics.notStarted || 0 }}</div>
                <div class="status-label">未开始</div>
              </div>
            </el-col>
            <el-col :span="4">
              <div class="status-item">
                <div class="status-number">{{ statistics.learning || 0 }}</div>
                <div class="status-label">学习中</div>
              </div>
            </el-col>
            <el-col :span="4">
              <div class="status-item">
                <div class="status-number">{{ statistics.passed || 0 }}</div>
                <div class="status-label">已通过</div>
              </div>
            </el-col>
            <el-col :span="4">
              <div class="status-item">
                <div class="status-number">{{ statistics.failed || 0 }}</div>
                <div class="status-label">未通过</div>
              </div>
            </el-col>
            <el-col :span="4">
              <div class="status-item">
                <div class="status-number">{{ statistics.expired || 0 }}</div>
                <div class="status-label">已过期</div>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- 详细列表 -->
        <div class="driver-list">
          <h4>司机培训详情</h4>
          <el-table :data="driverTrainingList">
            <el-table-column prop="driverNo" label="编号">
              <template slot-scope="scope">
                <span>{{ scope.row.driver.no }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="driverName" label="司机姓名">
              <template slot-scope="scope">
                <span>{{ scope.row.driver.name }}</span>
              </template>
            </el-table-column>

            <el-table-column prop="status" label="状态">
              <template slot-scope="scope">
                <el-tag :type="getStatusTag(scope.row.status)" size="mini">
                  {{ getStatusText(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column prop="learnStartTime" label="学习开始时间">
              <template slot-scope="scope">
                <span>{{
                  scope.row.learnResult && scope.row.learnResult.learnStartTime | dateFormat
                }}</span>
              </template>
            </el-table-column>

            <el-table-column prop="learnProgress" label="学习进度" width="120">
              <template slot-scope="scope">
                <el-progress :percentage="(scope.row.learnResult && scope.row.learnResult.learnProgress) || 0"
                  :stroke-width="6" :show-text="false" />
                <span style="margin-left: 8px">{{ scope.row.learnProgress || 0 }}%</span>
              </template>
            </el-table-column>

            <el-table-column prop="score" label="考试得分">
              <template slot-scope="scope">
                <span v-if="scope.row.learnResult && scope.row.learnResult.score !== null">{{
                  scope.row.learnResult.score }}分</span>
                <span v-else class="text-muted">未考试</span>
              </template>
            </el-table-column>

            <el-table-column prop="examCount" label="考试次数">
              <template slot-scope="scope">
                <span>{{ (scope.row.learnResult && scope.row.learnResult.examCount) || 0 }}次</span>
              </template>
            </el-table-column>

            <el-table-column label="人脸核验" width="120">
              <template slot-scope="scope">
                <el-tag :type="faceVerifyTag(scope.row)" size="mini">
                  {{ faceVerifyText(scope.row) }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column prop="latestFinishTime" label="截止时间">
              <template slot-scope="scope">
                {{ scope.row.latestFinishTime | dateFormat }}
              </template>
            </el-table-column>
            <el-table-column prop="createTime" label="操作" width="220">
              <template slot-scope="scope">
                <el-button type="text" size="mini" @click="handleView(scope.row)">考试结果</el-button>
                <el-button
                  v-if="scope.row.status === 'FACE_REVIEW'"
                  type="text"
                  size="mini"
                  @click="approveFace(scope.row)"
                >审核通过</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="handleClose">关闭</el-button>
      </div>
    </el-dialog>
    <!-- 查看对话框 -->
    <ExamViewDialog :visible.sync="viewDialogVisible" :exam-data="viewData" :user-answer-map="userAnswerMap"
      :face-captures="faceCaptures" />
  </div>
</template>

<script>
import { getDriverTrainingList, getTrainExam, approveDriverFaceReview } from "@/api/new_training";
import ExamViewDialog from "./ExamViewDialog.vue";
export default {
  name: "TrainingStatisticsDialog",
  components: {
    ExamViewDialog
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    trainingData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialogVisible: false,
      statistics: {},
      driverTrainingList: [],
      viewDialogVisible: false,
      viewData: {},
      userAnswerMap: null,
      faceCaptures: []
    };
  },

  watch: {
    visible: {
      handler(val) {
        this.dialogVisible = val;
        if (val) {
          this.loadStatistics();
        }
      },
      immediate: true
    }
  },
  methods: {
    // 加载统计数据
    async loadStatistics() {
      try {
        const res = await getDriverTrainingList(this.trainingData.id);
        const { list = [] } = res.data || {};
        this.statistics = res.data;
        this.driverTrainingList = list;
      } catch (error) {
        this.$message.error("加载统计数据失败");
        console.error(error);
      }
    },

    // 获取状态标签样式
    getStatusTag(status) {
      const statusMap = {
        NOT_STARTED: "info",
        LEARNING: "warning",
        PASSED: "success",
        FACE_REVIEW: "warning",
        FAILED: "danger",
        EXPIRED: "info"
      };
      return statusMap[status] || "info";
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        NOT_STARTED: "未开始",
        LEARNING: "学习中",
        PASSED: "已通过",
        FACE_REVIEW: "待审核",
        FAILED: "未通过",
        EXPIRED: "已过期"
      };
      return statusMap[status] || "未知";
    },

    // 关闭
    handleClose() {
      this.dialogVisible = false;
      this.$emit("update:visible", false);
    },

    examFaceCaptures(row) {
      const list = (row.learnResult && row.learnResult.captureList) || [];
      return list.filter((item) => item && item.isInExam);
    },

    faceVerifyText(row) {
      const list = this.examFaceCaptures(row);
      if (!list.length) {
        return "未抓拍";
      }
      if (list.some((item) => item.facePassed == null)) {
        return "核验中";
      }
      const passed = list.filter((item) => item.facePassed).length;
      return `通过 ${passed}/${list.length}`;
    },

    faceVerifyTag(row) {
      const list = this.examFaceCaptures(row);
      if (!list.length) {
        return "info";
      }
      if (list.some((item) => item.facePassed == null)) {
        return "info";
      }
      const passed = list.filter((item) => item.facePassed).length;
      if (passed === list.length) {
        return "success";
      }
      if (passed === 0) {
        return "danger";
      }
      return "warning";
    },

    approveFace(row) {
      this.$confirm("确认人脸核验审核通过？通过后该司机的培训考核记为已通过。", "提示", {
        type: "warning"
      })
        .then(() => approveDriverFaceReview(row.id))
        .then(() => {
          this.$message.success("已审核通过");
          this.loadStatistics();
        })
        .catch(() => {});
    },

    handleView(row) {
      getTrainExam(row.examId).then((res) => {
        this.viewData = res.data;
        if (row.learnResult) {
          this.userAnswerMap = row.learnResult.answers || null;
          this.faceCaptures = row.learnResult.captureList || [];
        } else {
          this.userAnswerMap = null;
          this.faceCaptures = [];
        }
        this.viewDialogVisible = true;
      });
    }
  }
};
</script>

<style lang="less" scoped>
.training-statistics {
  .statistics-header {
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e4e7ed;

    h3 {
      margin: 0 0 8px 0;
      color: #303133;
      font-size: 20px;
    }

    .statistics-meta {
      display: flex;
      gap: 20px;
      color: #909399;
      font-size: 14px;
    }
  }

  .statistics-overview {
    margin-bottom: 24px;

    .stat-card {
      text-align: center;
      padding: 10px;
      background: #f8f9fa;
      border-radius: 8px;
      border: 1px solid #e4e7ed;

      .stat-number {
        font-size: 24px;
        font-weight: 600;
        color: #409eff;
        margin-bottom: 8px;
      }

      .stat-label {
        color: #606266;
        font-size: 14px;
      }
    }
  }

  .status-distribution {
    margin-bottom: 24px;

    h4 {
      margin: 0 0 16px 0;
      color: #303133;
      font-size: 16px;
    }

    .status-item {
      text-align: center;
      padding: 16px;
      background: #f8f9fa;
      border-radius: 6px;
      border: 1px solid #e4e7ed;

      .status-number {
        font-size: 20px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 4px;
      }

      .status-label {
        color: #606266;
        font-size: 12px;
      }
    }
  }

  .driver-list {
    h4 {
      margin: 0 0 16px 0;
      color: #303133;
      font-size: 16px;
    }
  }

  .text-muted {
    color: #909399;
  }
}

.dialog-footer {
  text-align: right;
}
</style>
